import { Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DeleteOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import { SysAdminUserListItem, useGetAllUsersSysAdminQuery } from 'api/SysAdminApi';
import { UserAccessLevelPlanEnum, UserStatusEnum } from 'api/UsersApi';
import RemoveSysAdminUserModalComponent, {
  DELETE_SYS_ADMIN_USER_MODAL_NAME
} from 'components/SysAdmin/RemoveSysAdminUserModalComponent/RemoveSysAdminUserModalComponent';
import UpdateUserAffiliateSysAdminModalComponent, {
  UPDATE_USER_AFFILIATE_MODAL_NAME
} from 'components/SysAdmin/UpdateUserAffiliateSysAdminModalComponent/UpdateUserAffiliateSysAdminModalComponent';
import UpdateUserSysAdminModalComponent, {
  UPDATE_USER_SYS_ADMIN_MODAL_NAME
} from 'components/SysAdmin/UpdateUserSysAdminModalComponent/UpdateUserSysAdminModalComponent';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import './SysAdminUserListComponent.css';

const PAGE_SIZE = 10;

export const getSysAdminUserDetailPath = (sysAdminUser: SysAdminUserListItem) => {
  const resolvedPatientProfileId =
    sysAdminUser.patientProfileId || (sysAdminUser.recordType === 'patient_profile' ? sysAdminUser.id : undefined);

  if (resolvedPatientProfileId) {
    return `/sysadmin/patient-profiles/${resolvedPatientProfileId}`;
  }

  return `/sysadmin/users/${sysAdminUser.userId || sysAdminUser.id}`;
};

export const isSysAdminProfileOnlyRecord = (sysAdminUser: SysAdminUserListItem) => {
  return sysAdminUser.recordType === 'patient_profile' || (!sysAdminUser.userId && !!sysAdminUser.patientProfileId);
};

function SysAdminUserListComponent() {
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(1);

  const { data: sysAdminUserListData, isFetching } = useGetAllUsersSysAdminQuery({
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE
  });

  const onTableChanged = (pagination, _filters, _sorter) => {
    setCurrentPage(pagination.current || 1);
  };

  const renderStatus = (status: UserStatusEnum) => {
    const statusComponent = {
      [UserStatusEnum.ACTIVE]: <Tag color="success">Active</Tag>,
      [UserStatusEnum.UNVERIFIED_DECEASED]: <Tag color="default">Unverified Deceased</Tag>,
      [UserStatusEnum.DECEASED]: <Tag color="error">Deceased</Tag>
    };

    return statusComponent[status] || <Tag color="default">{status || 'Unknown'}</Tag>;
  };

  const getDisplayName = (sysAdminUser: SysAdminUserListItem) => {
    const profileFirstName = sysAdminUser.patientProfile?.firstName?.trim();
    const profileLastName = sysAdminUser.patientProfile?.lastName?.trim();
    const profileFullName = `${profileFirstName || ''} ${profileLastName || ''}`.trim();

    const userFirstName = sysAdminUser.firstName?.trim();
    const userLastName = sysAdminUser.lastName?.trim();
    const userFullName = `${userFirstName || ''} ${userLastName || ''}`.trim();

    return profileFullName || userFullName || sysAdminUser.email || sysAdminUser.patientProfile?.email || 'Unnamed profile';
  };

  const getDisplayEmail = (sysAdminUser: SysAdminUserListItem) => {
    return sysAdminUser.email || sysAdminUser.patientProfile?.email || 'No email on file';
  };

  const renderPortalAccessStatus = (sysAdminUser: SysAdminUserListItem) => {
    if (sysAdminUser.recordType === 'patient_profile' || sysAdminUser.portalAccessStatus === 'profile_only') {
      return <Tag color="default">Profile only</Tag>;
    }

    if (sysAdminUser.portalAccessStatus === 'invite_sent') {
      return <Tag color="processing">Invite sent</Tag>;
    }

    if (sysAdminUser.portalAccessStatus === 'registered') {
      return <Tag color="success">Registered</Tag>;
    }

    return <Tag color="default">Unknown</Tag>;
  };

  const renderLifecycleStatus = (sysAdminUser: SysAdminUserListItem) => {
    const lifecycleStatus = sysAdminUser.lifecycleStatus || sysAdminUser.status;

    if (lifecycleStatus) {
      return renderStatus(lifecycleStatus);
    }

    return <Tag color="default">N/A</Tag>;
  };

  const renderRelatedUsers = (users?: ({ id?: string | null; email?: string | null } | null)[] | null) => {
    if (!users?.length) {
      return null;
    }

    return users.map((user, index) => (
      <div key={`${user?.id || user?.email || 'related-user'}-${index}`}>{user?.email || 'No email on file'}</div>
    ));
  };

  const handleRemove = (user: SysAdminUserListItem) => {
    dispatch(openModal({ name: DELETE_SYS_ADMIN_USER_MODAL_NAME, data: { user } }));
  };

  const handleEditUser = (user: SysAdminUserListItem) => {
    dispatch(openModal({ name: UPDATE_USER_SYS_ADMIN_MODAL_NAME, data: { user } }));
  };

  const handleUpdateAffiliate = (user: SysAdminUserListItem) => {
    dispatch(openModal({ name: UPDATE_USER_AFFILIATE_MODAL_NAME, data: { user } }));
  };

  const columns: ColumnsType<SysAdminUserListItem> = [
    {
      title: 'User Name',
      key: 'name',
      ellipsis: true,
      render: (_, sysAdminUser) => {
        const detailPath = getSysAdminUserDetailPath(sysAdminUser);

        return <Link to={detailPath}>{getDisplayName(sysAdminUser)}</Link>;
      }
    },
    {
      title: 'Email',
      key: 'email',
      ellipsis: true,
      render: (_, sysAdminUser) => getDisplayEmail(sysAdminUser)
    },
    {
      title: 'Messages',
      key: 'messages',
      render: (_, sysAdminUser) => `${sysAdminUser.messagesPublishedCount ?? 0} (${sysAdminUser.messagesCount ?? 0})`
    },
    {
      title: 'Access Level',
      key: 'accessLevelPlan',
      dataIndex: 'accessLevelPlan',
      render: (accessLevelPlan: UserAccessLevelPlanEnum | null | undefined) => accessLevelPlan || 'N/A'
    },
    {
      title: 'Lifecycle Status',
      key: 'lifecycleStatus',
      render: (_, sysAdminUser) => renderLifecycleStatus(sysAdminUser)
    },
    {
      title: 'Portal Access',
      key: 'portalAccessStatus',
      render: (_, sysAdminUser) => renderPortalAccessStatus(sysAdminUser)
    },
    {
      title: 'Admin Info',
      key: 'info',
      render: (_, sysAdminUser) => (
        <>
          {(sysAdminUser.userAdmins?.length || 0) !== 0 && (
            <div>
              <strong>Administrators:</strong>
              {renderRelatedUsers(sysAdminUser.userAdmins)}
            </div>
          )}
          {(sysAdminUser.adminUsers?.length || 0) !== 0 && (
            <div>
              <strong>Administrator of:</strong>
              {renderRelatedUsers(sysAdminUser.adminUsers)}
            </div>
          )}
        </>
      )
    },
    {
      title: 'Affiliate',
      key: 'affiliate',
      dataIndex: 'affiliateCode',
      render: (affiliateCode: string | null | undefined) => affiliateCode || 'N/A'
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, sysAdminUser) => {
        const isProfileOnly = isSysAdminProfileOnlyRecord(sysAdminUser);

        if (isProfileOnly) {
          return (
            <Space size="small">
              <Tooltip title="Delete Patient Profile">
                <button
                  type="button"
                  aria-label="Delete patient profile"
                  onClick={() => handleRemove(sysAdminUser)}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <DeleteOutlined />
                </button>
              </Tooltip>
            </Space>
          );
        }

        return (
          <Space size="small">
            <Tooltip title="Edit User">
              <button
                type="button"
                aria-label="Edit user"
                onClick={() => handleEditUser(sysAdminUser)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <EditOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Affiliate">
              <button
                type="button"
                aria-label="Update affiliate"
                onClick={() => handleUpdateAffiliate(sysAdminUser)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <TeamOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Remove User">
              <button
                type="button"
                aria-label="Remove user"
                onClick={() => handleRemove(sysAdminUser)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  const getRowKey = (record: SysAdminUserListItem) => {
    return record.id || record.userId || record.patientProfileId || record.email || `${record.firstName || 'unknown'}-${record.lastName || 'unknown'}`;
  };

  return (
    <>
      <div className="sys-admin-user-list-component">
        <h3>Users ({sysAdminUserListData?.total || 0})</h3>

        <Table
          columns={columns}
          dataSource={sysAdminUserListData?.list || []}
          pagination={{
            defaultPageSize: PAGE_SIZE,
            current: currentPage,
            total: sysAdminUserListData?.total || 0
          }}
          onChange={onTableChanged}
          rowKey={getRowKey}
          bordered
          loading={isFetching}
        />
      </div>

      <UpdateUserAffiliateSysAdminModalComponent />
      <UpdateUserSysAdminModalComponent />
      <RemoveSysAdminUserModalComponent />
    </>
  );
}

export default SysAdminUserListComponent;
